import React from 'react';
import PropTypes from 'prop-types';
import merge from 'lodash/merge';
import CssBaseline from '@material-ui/core/CssBaseline';
import ConnectedThemeProvider from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/ThemeProvider';
import { darkWhite, gray, green, greenHover, lightGray, white } from './Colors';

const theme = {
  typography: {
    allVariants: {
      color: gray,
    },
    body1: {
      color: gray,
    },
    body2: {
      color: gray,
    },
    button: {
      color: gray,
    },
    caption: {
      color: gray,
    },
    h1: {
      fontWeight: 'bold',
      color: gray,
    },
    h2: {
      fontWeight: 'bold',
      color: gray,
    },
    h3: {
      fontWeight: 'bold',
      color: gray,
    },
    h4: {
      fontWeight: 'bold',
      color: gray,
    },
    h5: {
      fontWeight: 'bold',
      color: gray,
    },
    h6: {
      fontWeight: 'bold',
      color: gray,
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: {
          background: white,
        },
      },
    },
  },
};

const overrideTheme = (theme) => {
  merge(theme.overrides, {
    MuiButton: {
      root: {
        borderRadius: 0,
        width: '100%',
        color: gray,
      },
      contained: {
        boxShadow: 'none',
        '.Mui-focusVisible': {
          boxShadow: 'none',
        },
      },
      text: {
        fontWeight: 'bold',
      },
      label: {
        fontWeight: 'bold',
      },
    },
    MuiIcon: {
      root: {
        color: gray,
      },
    },
    Option: {
      root: {
        width: '100%',
        '&.selected': {
          backgroundColor: lightGray,
        },
        '&.disabled': {
          pointerEvents: 'none',
        },
      },
    },
    DefaultLayout: {
      root: {
        paddingLeft: 0,
        paddingRight: 0,
      },
      container: {
        order: 1,
        '&.fullSize': {
          height: 'auto',
          flex: 1,
        },
        '&.reducedSize': {
          width: 'auto',
          marginLeft: 0,
        },
      },
      shoppingCart: {
        order: 3,
      },
      outerContainer: {
        background: darkWhite,
        marginRight: 0,
        [theme.breakpoints.up('md')]: {
          marginBottom: 0,
        },
        '&.withBottomList': {
          flexDirection: 'column',
        },
        [theme.breakpoints.down('xs')]: {
          margin: 0,
        },
        [theme.breakpoints.down('md')]: {
          marginBottom: 0,
        },
      },
      productsList: {
        '&.vertical': {
          height: '100%',
          width: 300,
          top: 0,
          left: 0,
          paddingLeft: 24,
          position: 'relative',
        },
        '&.horizontal': {
          order: 2,
          height: 'auto',
          position: 'relative',
          [theme.breakpoints.down('xs')]: {
            bottom: 0,
          },
          [theme.breakpoints.up('sm')]: {
            width: 'auto',
          },
        },
        background: white,
      },
      visualization: {
        '&.withBottomList': {
          height: '100% !important',
        },
      },
    },
    CreatorAlignTarget: {
      root: {
        [theme.orientation.portrait]: {
          maxHeight: '100%',
        },
      },
    },
    FullScreenView: {
      button: {
        display: 'none',
      },
      buttonView: {
        display: 'none',
      },
    },
    ToolbarList: {
      iconHover: {
        color: gray,
      },
    },
    ProductPart: {
      root: {
        [theme.breakpoints.up('md')]: {
          width: 300,
        },
        '&.vertical': {
          flexDirection: 'row',
        },
        '&.horizontal': {
          padding: 10,
        },
      },
      content: {
        '&.vertical': {
          width: 'auto',
          float: 'left',
          padding: '0 0 5px 0',
        },
      },
      icon: {
        display: 'none',
      },
      rootTypography: {
        maxWidth: '100%',
        padding: '12px 0',
        fontWeight: 'bold',
        lineHeight: '1',
        paddingTop: 12,
      },
      marker: {
        display: 'none',
      },
    },
    ProductPartsList: {
      root: {
        '&.vertical': {
          paddingTop: 0,
          marginLeft: 0,
          paddingLeft: 0,
          float: 'none',
          [theme.breakpoints.down('md')]: {
            paddingTop: 0,
          },
        },
      },
    },
    OptionsList: {
      dialogStyle: {
        marginTop: 32,
        marginLeft: 0,
        marginRight: 0,
        [theme.orientation.landscape]: {
          marginLeft: 180,
        },
        [theme.breakpoints.up('md')]: {
          marginLeft: 0,
        },
        [theme.breakpoints.down('sm')]: {
          maxWidth: '100%',
        },
        '& .MuiDialog-scrollPaper': {
          display: 'block',
        },
        '& .MuiDialog-paper': {
          [theme.breakpoints.down('sm')]: {},
        },
        '& .MuiDialog-paperFullWidth': {
          [theme.breakpoints.down('xs')]: {
            width: 'calc(100% - 32px)',
            marginLeft: 16,
            marginRight: 16,
          },
        },
        '& .MuiDialog-container .MuiDialogTitle-root': {
          [theme.breakpoints.down('xs')]: {
            padding: 16,
          },
          '& .MuiIcon-root': {
            top: 24,
          },
        },
        '& .MuiDialog-container .MuiDialogContent-root': {
          [theme.breakpoints.down('xs')]: {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    AmountPrice: {
      root: {
        bottom: 24,
        right: 24,
        width: 'auto',
        [theme.breakpoints.between('xs', 'md')]: {
          width: 'auto',
        },
        [theme.breakpoints.up('sm')]: {
          width: 360,
        },
        [theme.orientation.portrait]: {
          position: 'relative',
          bottom: 0,
          right: 0,
        },
      },
      card: {
        [theme.orientation.portrait]: {
          flexDirection: 'column',
        },
        '&:last-child': {
          [theme.breakpoints.down('xs')]: {
            padding: 16,
          },
        },
        [theme.breakpoints.up('sm')]: {
          margin: 0,
        },
        [theme.breakpoints.between('xs', 'md')]: {
          marginTop: 0,
        },
      },
      cardUnboxed: {
        padding: 16,
      },
      configurationButton: {
        padding: 6,
        minWidth: 'auto',
        width: 'auto',
      },
    },
    CallToAction: {
      container: {
        top: -5,
      },
      rightContainer: {
        top: -5,
      },
    },
    PdfDownload: {
      activeButton: {
        backgroundColor: green,
        '&:hover': {
          backgroundColor: greenHover,
        },
      },
    },
    AcceptPrivacy: {
      checkboxLabel: {
        paddingTop: 8,
      },
    },
    CustomDialog: {
      MuiStyledDialog: {
        '& .MuiDialog-container': {
          '& .MuiDialogTitle-root': {
            paddingTop: 20,
            paddingBottom: 15,
          },
        },
      },
    },
    CustomDialogHeader: {
      closeButton: {
        marginRight: theme.spacing(2),
        marginTop: theme.spacing(2),
      },
    },
    MuiDialogContent: {
      root: {
        flex: '1 1 auto',
        padding: '8px 24px',
        overflowY: 'auto',
        '&:first-child': {
          paddingTop: 8,
        },
      },
    },
    MainButton: {
      root: {
        height: 40,
      },
    },
    MuiTouchRipple: {
      root: {
        display: 'none',
      },
    },
  });
  merge(theme, {
    palette: {
      primary: {
        main: green,
        darkText: gray,
      },
    },
    buttons: {
      mainBtn: {
        backgroundColor: green,
      },
      mainBtnHover: {
        backgroundColor: greenHover,
      },
    },
  });
};

export default function ThemeProvider({ children }) {
  return (
    <ConnectedThemeProvider customTheme={theme} withTheme={overrideTheme}>
      <CssBaseline />
      {children}
    </ConnectedThemeProvider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
};
